import { Input as UIKitInput } from "@credit-acceptance/uikit";
import type { Checkbox as CheckboxPrimitive } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "../../../lib/utils";

type CheckboxProperties = ComponentProps<typeof CheckboxPrimitive.Root>;

/**
 * Drop-in replacement for the shadcn `ui/checkbox`, rendering uikit's Checkbox.
 *
 * uikit renders the same Radix primitive underneath, so `role`, keyboard
 * behaviour and the `aria-*` passthrough are unchanged; only the visual token
 * set differs. It ships at `size-5`, which the workbench overrides back to the
 * `size-4` box the shadcn version used.
 *
 * uikit wraps the control in its own `<label>`. It stays empty here — callers
 * render their own `<label htmlFor>` sibling — so the accessible name still
 * comes from that sibling.
 */
function Checkbox({ checked, onCheckedChange, className, id, ...properties }: CheckboxProperties) {
  const uikitProperties = {
    kind: "checkbox",
    selected: checked === true,
    indeterminate: checked === "indeterminate",
    // uikit puts this on the input's `value`; the id keeps it unique per field.
    checkboxValue: id ?? "on",
    id,
    className: cn("size-4 [&_svg]:size-3.5", className),
    onChange: (next: boolean) => onCheckedChange?.(next),
    ...properties,
    // See the note in ./input.tsx — uikit's props come from an older
    // @types/react, so the DOM attribute types need reconciling.
  } as ComponentProps<typeof UIKitInput>;

  return <UIKitInput {...uikitProperties} />;
}

export { Checkbox };
